/**
 * InstallPromptOverlay.js
 *
 * 회원가입 직후 1회 표시되는 앱 설치 유도 오버레이.
 * - Android: beforeinstallprompt가 있으면 설치 버튼, 없으면 Chrome 메뉴 안내
 * - iOS: Safari / Chrome 별 홈 화면 추가 안내 + URL 복사 버튼
 * - 설치하기 / 웹으로 하기 중 하나를 선택하면 localStorage에 플래그 저장 후 사라짐
 */
import { useState, useEffect } from 'react';

const isAndroid = () => /Android/.test(navigator.userAgent);
const isIOS = () => /iPhone|iPad|iPod/.test(navigator.userAgent);
const isIOSSafari = () => isIOS() && /Safari\//.test(navigator.userAgent) && !/CriOS\//.test(navigator.userAgent);
const isIOSChrome = () => isIOS() && /CriOS\//.test(navigator.userAgent);
const isStandalone = () =>
  window.matchMedia('(display-mode: standalone)').matches ||
  window.navigator.standalone === true;

export const shouldShowInstallPrompt = () => {
  if (isStandalone()) return false;
  if (localStorage.getItem('installPromptShown')) return false;
  if (!isAndroid() && !isIOS()) return false;
  return true;
};

export default function InstallPromptOverlay({ installPrompt, onInstallPromptUsed, onDismiss }) {
  const [copied, setCopied] = useState(false);
  const [installing, setInstalling] = useState(false);
  const [visible, setVisible] = useState(false);

  // 마운트 시 fade-in
  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 50);
    return () => clearTimeout(t);
  }, []);

  const dismiss = () => {
    localStorage.setItem('installPromptShown', '1');
    onDismiss();
  };

  const handleInstallAndroid = async () => {
    if (!installPrompt) {
      // prompt 없음 → Chrome 메뉴 안내
      alert('Chrome 주소창 오른쪽 메뉴(⋮)를 누른 뒤 "홈 화면에 추가"를 선택해주세요.');
      return;
    }
    setInstalling(true);
    try {
      await installPrompt.prompt();
      const { outcome } = await installPrompt.userChoice;
      if (outcome === 'accepted') onInstallPromptUsed?.();
    } catch (e) {
      console.warn('[InstallPromptOverlay] prompt error:', e);
    }
    dismiss();
  };

  const handleCopyURL = () => {
    const url = window.location.origin;
    navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 3000);
    }).catch(() => {
      const el = document.createElement('textarea');
      el.value = url;
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
      setCopied(true);
      setTimeout(() => setCopied(false), 3000);
    });
  };

  const hasPrompt = !!installPrompt;
  const android = isAndroid();
  const ios = isIOS();
  const iosSafari = isIOSSafari();

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 99999,
      background: 'rgba(0,0,0,0.55)',
      display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
      opacity: visible ? 1 : 0,
      transition: 'opacity 0.25s',
    }}>
      <div style={{
        width: '100%',
        maxWidth: '480px',
        background: '#fff',
        borderRadius: '24px 24px 0 0',
        padding: '28px 24px 40px',
        boxShadow: '0 -8px 40px rgba(0,0,0,0.15)',
        transform: visible ? 'translateY(0)' : 'translateY(40px)',
        transition: 'transform 0.3s cubic-bezier(0.34,1.56,0.64,1)',
      }}>
        {/* 드래그 핸들 */}
        <div style={{ width: 40, height: 4, background: '#e2e8f0', borderRadius: 4, margin: '0 auto 20px' }} />

        {/* 앱 아이콘 + 제목 */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 20 }}>
          <div style={{
            width: 64, height: 64, borderRadius: 16, flexShrink: 0,
            background: 'linear-gradient(135deg, #3b82f6, #8b5cf6)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 32, boxShadow: '0 4px 12px rgba(59,130,246,0.3)',
          }}>📅</div>
          <div>
            <div style={{ fontWeight: 700, fontSize: 17, color: '#1e293b', marginBottom: 4 }}>
              홈 화면에 앱 추가하기
            </div>
            <div style={{ fontSize: 13, color: '#64748b', lineHeight: 1.5 }}>
              {android ? '빠르게 접근하고 알림도 받아보세요' : '홈 화면에 추가하면 앱처럼 사용할 수 있어요'}
            </div>
          </div>
        </div>

        {/* ── Android ── */}
        {android && (
          <>
            {!hasPrompt && (
              <div style={{
                background: '#f8fafc', borderRadius: 12,
                padding: '12px 16px', marginBottom: 16,
                fontSize: 13, color: '#475569', lineHeight: 1.7,
                border: '1px solid #e2e8f0',
              }}>
                <strong>Chrome 메뉴로 설치하기</strong><br />
                주소창 오른쪽 <strong>⋮</strong> 버튼 → <strong>"홈 화면에 추가"</strong> 선택
              </div>
            )}
            <button
              onClick={handleInstallAndroid}
              disabled={installing}
              style={{
                width: '100%', padding: '15px',
                background: installing ? '#93c5fd' : 'linear-gradient(135deg, #3b82f6, #6366f1)',
                color: '#fff', border: 'none', borderRadius: 14,
                fontSize: 16, fontWeight: 700, cursor: installing ? 'not-allowed' : 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                boxShadow: '0 4px 16px rgba(59,130,246,0.35)',
                marginBottom: 12,
              }}
            >
              {installing ? '설치 중...' : hasPrompt ? '📲 앱 설치하기' : '📲 홈 화면에 추가하기'}
            </button>
          </>
        )}

        {/* ── iOS ── */}
        {ios && (
          <>
            <div style={{
              background: '#f0f9ff', borderRadius: 12,
              padding: '14px 16px', marginBottom: 16,
              border: '1px solid #bae6fd',
            }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#0369a1', marginBottom: 8 }}>
                홈 화면 추가 방법
              </div>
              {iosSafari ? (
                <ol style={{ margin: 0, paddingLeft: 20, fontSize: 13, color: '#334155', lineHeight: 1.8 }}>
                  <li>하단 공유 버튼 <strong>□↑</strong> 탭</li>
                  <li><strong>"홈 화면에 추가"</strong> 선택</li>
                  <li><strong>"추가"</strong> 버튼 탭</li>
                </ol>
              ) : isIOSChrome() ? (
                <ol style={{ margin: 0, paddingLeft: 20, fontSize: 13, color: '#334155', lineHeight: 1.8 }}>
                  <li>주소창 오른쪽 <strong>...</strong> 버튼 탭</li>
                  <li><strong>"홈 화면에 추가"</strong> 선택</li>
                  <li><strong>"추가"</strong> 버튼 탭</li>
                </ol>
              ) : (
                <ol style={{ margin: 0, paddingLeft: 20, fontSize: 13, color: '#334155', lineHeight: 1.8 }}>
                  <li>Safari 또는 Chrome에서 이 링크를 열어주세요</li>
                  <li>공유/메뉴 버튼을 탭한 뒤 <strong>"홈 화면에 추가"</strong> 선택</li>
                </ol>
              )}
            </div>

            {/* 링크 복사 버튼 (인앱 브라우저용) */}
            <button
              onClick={handleCopyURL}
              style={{
                width: '100%', padding: '13px',
                background: copied ? '#f0fdf4' : '#f8fafc',
                color: copied ? '#16a34a' : '#475569',
                border: `1px solid ${copied ? '#bbf7d0' : '#e2e8f0'}`,
                borderRadius: 12, fontSize: 14, fontWeight: 600,
                cursor: 'pointer', marginBottom: 12,
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                transition: 'all 0.2s',
              }}
            >
              {copied ? '✓ 복사됨!' : '🔗 링크 복사하기'}
            </button>
          </>
        )}

        {/* 웹으로 하기 버튼 */}
        <button
          onClick={dismiss}
          style={{
            width: '100%', padding: '13px',
            background: 'none', border: 'none',
            color: '#94a3b8', fontSize: 14, cursor: 'pointer',
            fontWeight: 500,
          }}
        >
          웹으로 할래요
        </button>
      </div>
    </div>
  );
}
